// Reporting / Analytics export API service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config and auth error handling
const apiClient = createApiClient(API_BASE_URL);

export type ReportFormat = 'pdf' | 'excel' | 'csv' | 'json';

export interface Report {
  id: string;
  title: string;
  type: 'analytics' | 'financial' | 'performance' | 'custom';
  status: 'generating' | 'completed' | 'failed' | 'scheduled';
  createdAt: string;
  completedAt?: string;
  createdBy: string;
  description: string;
  format: ReportFormat;
  size: string;
  dataRange: {
    startDate: string;
    endDate: string;
  };
  metrics: {
    totalViews: number;
    totalClicks: number;
    totalShares: number;
    totalRevenue: number;
    totalCampaigns: number;
    totalPRs: number;
  };
  tags: string[];
  isFavorite: boolean;
  lastViewed?: string;
  downloadCount: number;
  sharedWith: string[];
  fileUrl?: string;
}

export interface ReportStats {
  total: number;
  completed: number;
  totalDownloads: number;
  favorites: number;
}

export interface ReportsResponse {
  success: boolean;
  reports: Report[];
  stats: ReportStats;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Get all reports with filters
export const getReports = async (
  type?: string,
  status?: string,
  format?: string,
  search?: string,
  page: number = 1,
  limit: number = 100
): Promise<ReportsResponse> => {
  const params: any = { page, limit };
  if (type && type !== 'all') params.type = type;
  if (status && status !== 'all') params.status = status;
  if (format && format !== 'all') params.format = format;
  if (search) params.search = search;

  const response = await apiClient.get('/api/reports', { params });
  return response.data;
};

// Get a single report
export const getReport = async (id: string): Promise<{ success: boolean; report: Report }> => {
  const response = await apiClient.get(`/api/reports/${id}`);
  return response.data;
};

// Generate a new report
export const generateReport = async (
  title: string,
  type: 'analytics' | 'financial' | 'performance' | 'custom',
  format: ReportFormat,
  description: string,
  startDate: string,
  endDate: string
): Promise<{ success: boolean; report: Report }> => {
  const response = await apiClient.post('/api/reports/generate', {
    title,
    type,
    format,
    description,
    startDate,
    endDate
  });
  return response.data;
};

// Download a report
export const downloadReport = async (id: string, format: ReportFormat): Promise<Blob | any> => {
  const response = await apiClient.get(`/api/reports/${id}/download`, {
    responseType: format === 'json' ? 'json' : 'blob',
  });
  return response.data;
};

// Toggle favorite status
export const toggleFavorite = async (id: string): Promise<{ success: boolean; isFavorite: boolean }> => {
  const response = await apiClient.patch(`/api/reports/${id}/favorite`);
  return response.data;
};

// Delete a report
export const deleteReport = async (id: string): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete(`/api/reports/${id}`);
  return response.data;
};

// Legacy function for backward compatibility
export const exportAnalyticsReport = async (
  format: ReportFormat,
  dateRange: string
): Promise<Blob | any> => {
  const response = await apiClient.get(`/api/pr/analytics/export`, {
    params: { format, dateRange },
    responseType: format === 'json' ? 'json' : 'blob',
  });
  return response.data;
};
